import React, { useCallback, useMemo } from "react";
import { Grid, Table, TableHeaderRow } from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";

const StyledGridContainer = styled.div<{ width?: string }>`
  width: ${props => props.width || "100%"};

  .table-responsive {
    background-color: rgb(245, 247, 250);

    .table {
      td {
        font-size: 11px;
        word-break: break-word;
        padding: 0 5px;
        vertical-align: middle;
      }

      margin-bottom: 0;

      tr td:first-child,
      th:first-child {
        padding-left: 20px;
      }

      th > div > div {
        text-transform: uppercase;
      }

      th {
        padding: 5px;
        max-width: 100px;
        word-break: break-word;
        background-color: rgb(250, 251, 252);
      }
    }
  }

  .dx-g-bs4-table-head {
    font-size: 11px;
  }

  .even {
    background-color: rgb(250, 251, 252) !important;
  }

  .odd {
    background-color: rgb(245, 247, 250) !important;
  }
`;

export interface Identifiable {
  id: string | number;
}

export interface SimpleGridColumn<T = any> {
  name: keyof T | string;
  title: string;
  getCellValue?: (row: T) => React.ReactNode;
}

export interface RowAction<T = any> {
  icon: any;
  tooltip?: string;
  variant?: string;
  onClick: (row: T) => void;
  visible?: (row: T) => boolean;
}

export interface SimpleGridProps<T extends Identifiable> {
  rows: T[];
  columns: SimpleGridColumn<T>[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  customActions?: RowAction<T>[];
  actionsColumnTitle?: string;
  width?: string;
  getRowId?: (row: T) => string | number;
  hideActions?: boolean;
}

const defaultGetRowId = <T extends Identifiable>(row: T): string | number => row.id;

export function SimpleGrid<T extends Identifiable>({
  rows,
  columns,
  onEdit,
  onDelete,
  customActions = [],
  actionsColumnTitle = "Actions",
  width = "100%",
  getRowId = defaultGetRowId,
  hideActions = false,
}: SimpleGridProps<T>) {
  const showActionsColumn = !hideActions && (onEdit || onDelete || customActions.length > 0);

  const gridColumns = useMemo(() => {
    const cols = columns.map(col => ({
      name: col.name as string,
      title: col.title,
    }));

    if (showActionsColumn) {
      cols.push({
        name: "__actions",
        title: actionsColumnTitle,
      });
    }

    return cols;
  }, [columns, showActionsColumn, actionsColumnTitle]);

  const RowComponent = useCallback(
    ({ row, ...restProps }: any) => {
      const rowIndex = rows.indexOf(row);
      const className = rowIndex % 2 === 0 ? "odd" : "even";
      return <Table.Row {...restProps} className={clsx(className, "h-[40px]")} />;
    },
    [rows]
  );

  const CellComponent = useCallback(
    ({ column, row, ...restProps }: any) => {
      if (column.name === "__actions" && showActionsColumn) {
        return (
          <Table.Cell {...restProps}>
            {customActions
              .filter(action => !action.visible || action.visible(row))
              .map((action, index) => (
                <Button
                  key={`custom-${index}`}
                  data-tip="React-tooltip"
                  data-for={action.tooltip}
                  size="sm"
                  variant={action.variant || "light"}
                  onClick={() => action.onClick(row)}
                  className="mr-1">
                  <FontAwesomeIcon icon={action.icon} />
                </Button>
              ))}

            {onEdit && (
              <Button
                data-tip="React-tooltip"
                data-for="edit"
                size="sm"
                variant="light"
                onClick={() => onEdit(row)}
                className="mr-1">
                <FontAwesomeIcon icon={faPencilAlt} />
              </Button>
            )}

            {onDelete && (
              <Button
                data-tip="React-tooltip"
                data-for="delete"
                size="sm"
                variant="light"
                onClick={() => onDelete(row)}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </Button>
            )}
          </Table.Cell>
        );
      }

      const columnDef = columns.find(col => col.name === column.name);
      if (columnDef?.getCellValue) {
        return <Table.Cell {...restProps}>{columnDef.getCellValue(row)}</Table.Cell>;
      }

      return <Table.Cell {...restProps} />;
    },
    [showActionsColumn, customActions, onEdit, onDelete, columns]
  );

  return (
    <StyledGridContainer width={width}>
      <Grid rows={rows} columns={gridColumns} getRowId={getRowId}>
        <Table cellComponent={CellComponent} rowComponent={RowComponent} />
        <TableHeaderRow />
      </Grid>
    </StyledGridContainer>
  );
}
